import React, { useEffect, useState, useRef } from "react";
import { VariableSizeList } from "react-window";
import Autocomplete from "@mui/material/Autocomplete";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Box,
  Grid,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel,
  Modal,
} from "@mui/material";
import {
  DeleteOutlined as DeleteOutlinedIcon,
  Delete,
  Add,
  PhotoCamera,
  Home as HomeIcon,
} from "@mui/icons-material";
import "./SellerProfileEditPage.scss";
import { ErrorLogger } from "../../../utils/Logger";
import { compressImage } from "../../../utils/FileCompressionUtils";
import { indianInstitutes } from "../../../assets/json/institutes";
import { degrees as customDegrees } from "../../../assets/json/degrees";
import { SellerAPIHandler } from "../../../services/NetworkClient/BackendAPIs/SellerAPIhandler";
import LocationForm from "../SellerRegistrationPageComponents/LocationForm/LocationForm";
import GGButton from "../../CommonComponents/GGButton/GGButton";
import { SessionManager } from "../../../services/SessionManager/SessionManager";
import { v4 as uuidv4 } from "uuid";
import Navbar from "../../CommonComponents/Navbar/Navbar";
import { SubcategoryAPIHandler } from "../../../services/NetworkClient/BackendAPIs/SubcategoryAPIhandler";
import GGLoaderButton from "../../CommonComponents/GGLoaderButton/GGLoaderButton";
import { Seller } from "../../../Models/Seller";
import SnackBar, {
  SnackTypes,
} from "../../UtilityComponents/SnackBar/SnackBar";
import { Category } from "../../../Models/Category";
import { Subcategory } from "../../../Models/SubCategory";
import { CategoryAPIHandler } from "../../../services/NetworkClient/BackendAPIs/CategoryAPIHandler";
import { ResumableUploadTask } from "../../../services/UploadClient/ResumableUploadTask";
import { UploadPurpose } from "../../../services/NetworkClient/BackendAPIs/UploadAPIHandler";
import Footer from "../../CommonComponents/Footer/Footer";
import GGPlaceholderImage from "../../CommonComponents/GGPlaceholderImage/GGPlaceholderImage";
import GGDialogueBox, { dialogueType } from "../../UtilityComponents/GGDialogueBox/GGDialogueBox";
import UserAgreementModal from "../../UtilityComponents/UserAgreementModal/UserAgreementModal";
import CongratulationsPageForGigReg from "../../CommonComponents/CongratulationsPageForGigReg/CongratulationsPageForGigReg";

interface EducationProps {
  id: string;
  institute: string;
  degree: string;
  passoutYear: string;
}

interface ExperienceProps {
  id: string;
  company: string;
  designation: string;
  employmentType: string;
  startDate: string;
  endDate?: string;
  currentlyWorking?: boolean;
}

interface SkillProps {
  id: string;
  category: string;
  subcategory: string;
  tags: string[];
  subcategories: Subcategory[];
  projects: {
    id: string;
    title: string;
    description: string;
    startDate: string;
    endDate: string;
    link: string;
  }[];
}

interface Location {
  pincode: string;
  region: string;
  country: string;
  state: string;
  languages: string[];
}

interface SellerFormData {
  location: Location;
}

const LISTBOX_PADDING = 8;

function renderRow(props: any) {
  const { data, index, style } = props;
  const option = data[index];

  return React.cloneElement(option, {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      paddingRight: 8,
    },
    children: (
      <Tooltip title={option.props.children}>
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {option.props.children}
        </div>
      </Tooltip>
    ),
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>(
  function OuterElementType(props, ref) {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  }
);

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef(function ListboxComponent(
  props: React.HTMLAttributes<HTMLElement>,
  ref: React.Ref<HTMLDivElement>
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={() => itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

interface NewSellerProfileEditPageProps {
  isNewSeller?: boolean;
  onRegistrationSuccess?: () => void;
}

const SellerProfileEditPage: React.FC<NewSellerProfileEditPageProps> = ({
  isNewSeller = false,
  onRegistrationSuccess,
}) => {
  const [gender, setGender] = useState<string>("");
  const [profileImage, setProfileImage] = useState<string>("");
  const [coverImage, setCoverImage] = useState<string>("");
  const [uploadedProfileImage, setUploadedProfileImage] = useState<
    File | undefined
  >(undefined);
  const [uploadedCoverImage, setUploadedCoverImage] = useState<
    File | undefined
  >(undefined);
  const [institutes, setInstitutes] = useState<string[]>([]);
  const [degrees, setDegrees] = useState<string[]>([]);
  const [categoriesList, setCategoriesList] = useState<Category[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [categoriesMap, setCategoriesMap] = useState<
    Map<string, Subcategory[]>
  >(new Map());

  const [expandedSkills, setExpandedSkills] = useState<string[]>([]);
  const [skills, setSkills] = useState<SkillProps[]>([
    {
      id: "1",
      category: "",
      subcategory: "",
      tags: [],
      subcategories: [],
      projects: [
        {
          id: "1",
          title: "",
          description: "",
          startDate: "",
          endDate: "",
          link: "",
        },
      ],
    },
  ]);

  const [education, setEducation] = useState<EducationProps[]>([]);

  const [workExperience, setWorkExperience] = useState<ExperienceProps[]>([]);

  const [activeStep, setActiveStep] = useState(0);

  const [sellerFormData, setSellerFormData] = useState<SellerFormData>({
    location: {
      pincode: "",
      region: "",
      country: "",
      state: "",
      languages: [],
    },
  });

  const [fullName, setFullName] = useState<string>("");
  const [personalDescription, setPersonalDescription] = useState<string>("");
  const [tagline, setTagline] = useState<string>("");
  const [occupation, setOccupation] = useState<string>("");
  const [contactNumber, setContactNumber] = useState<string>("");
  const [contactNumberError, setContactNumberError] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<string>("");

  const [isDirty, setIsDirty] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showRefreshDialog, setShowRefreshDialog] = useState(false);
  const skipUnsavedChangesWarningRef = useRef(false);
  
  // New seller registration states
  const [showUserAgreementModal, setShowUserAgreementModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const snackBarRef = React.useRef<{ showSnackbar(): any }>(null);
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error,
  });

  const profileFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const coverFileInputRef = React.useRef<HTMLInputElement | null>(null);
  
  // Refs for scrolling to newly added items
  const educationRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({});
  const experienceRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({});
  const skillRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // Track newly added item IDs for scrolling
  const [newlyAddedEducationId, setNewlyAddedEducationId] = useState<string | null>(null);
  const [newlyAddedExperienceId, setNewlyAddedExperienceId] = useState<string | null>(null);
  const [newlyAddedSkillId, setNewlyAddedSkillId] = useState<string | null>(null);

  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message, state });
    snackBarRef.current?.showSnackbar();
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);

      // Validate required form fields before saving
      const validationErrors: string[] = [];
      
      // Required field validations
      if (!fullName.trim()) validationErrors.push("Full Name");
      if (!dateOfBirth) validationErrors.push("Date of Birth");
      if (!gender) validationErrors.push("Gender");
      if (!occupation) validationErrors.push("Occupation");
      if (!personalDescription.trim()) validationErrors.push("Personal Description");
      if (!tagline.trim()) validationErrors.push("Tagline");
      
      // Validate mobile number (required and format)
      if (!contactNumber) {
        validationErrors.push("Contact Number");
      } else if (!/^[6-9]\d{9}$/.test(contactNumber)) {
        validationErrors.push("Valid Mobile Number (10 digits starting with 6-9)");
        setContactNumberError("Invalid mobile number");
      }
      
      // Location validations
      if (!sellerFormData.location.pincode || sellerFormData.location.pincode.length !== 6) {
        validationErrors.push("Valid Pincode (6 digits)");
      }
      if (!sellerFormData.location.region) validationErrors.push("Region");
      if (!sellerFormData.location.country) validationErrors.push("Country");
      if (!sellerFormData.location.state) validationErrors.push("State");
      if (sellerFormData.location.languages.length === 0) validationErrors.push("At least one Language");

      // If there are validation errors, show error message and prevent save
      if (validationErrors.length > 0) {
        const errorMessage = `Please fill in the following required fields: ${validationErrors.join(", ")}`;
        showSnackbar(errorMessage, SnackTypes.error);
        setIsSaving(false);
        return;
      }

      if (sellerObj) {
        // Handle profile and cover image changes first
        await handleProfileImageChange();
        await handleCoverImageChange();

        // Update sellerObj fields (matching old ProfileEdit.tsx approach)
        sellerObj.fullName = fullName;
        sellerObj.personalDescription = personalDescription;
        sellerObj.tagline = tagline;
        sellerObj.contactNumber = contactNumber;
        sellerObj.occupation = occupation;
        sellerObj.location = {
          country: sellerFormData.location.country,
          state: sellerFormData.location.state,
          region: sellerFormData.location.region,
          pincode: parseInt(sellerFormData.location.pincode, 10),
          languages: sellerFormData.location.languages,
        };
        // Filter and map education - only include complete entries (optional section)
        sellerObj.education = education
          .filter((edu) => edu.institute && edu.degree && edu.passoutYear)
          .map((edu) => ({
            institute: edu.institute,
            degree: edu.degree,
            passoutYear: parseInt(edu.passoutYear, 10),
          }));
        
        // Filter and map work experience - only include complete entries (optional section)
        sellerObj.workExperience = workExperience
          .filter((exp) => exp.company && exp.designation && exp.employmentType && exp.startDate)
          .map((exp) => ({
            company: exp.company,
            designation: exp.designation,
            employmentType: exp.employmentType,
            startDate: new Date(exp.startDate),
            endDate: exp.endDate ? new Date(exp.endDate) : undefined,
            currentlyWorking: exp.currentlyWorking || false,
          }));
        
        // Filter and map skills - only include complete entries (optional section)
        sellerObj.skills = skills
          .filter((skill) => skill.category && skill.subcategory)
          .map((skill) => ({
            category: skill.category,
            subcategory: skill.subcategory,
            tags: skill.tags,
            projects: skill.projects
              .filter((proj) => proj.title && proj.description)
              .map((proj) => ({
                projectTitle: proj.title,
                projectDescription: proj.description,
                projectLink: proj.link,
                startDate: proj.startDate ? new Date(proj.startDate) : new Date(),
                endDate: proj.endDate ? new Date(proj.endDate) : new Date(),
              })),
            extraNotes:
              sellerObj.skills?.find((s) => s.category === skill.category)
                ?.extraNotes || "",
          }));
        sellerObj.gender = gender;
        sellerObj.dateOfBirth = dateOfBirth;

        // Create a clean object without buyer field to avoid backend validator rejection
        // Backend validator rejects buyer field, but backend controller needs seller.buyer from DB
        const { buyer, ...updatePayload } = sellerObj as any;
        
        await SellerAPIHandler.shared.updateSeller(updatePayload);
      }

      setIsDirty(false);
      setIsSaving(false);
      showSnackbar("Changes saved successfully!", SnackTypes.success);
    } catch (error: any) {
      // Extract error message from the error object
      // NetworkErrorHandler.handleError returns a string message
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.response?.data?.message || "Profile updation failed!";
      
      showSnackbar(errorMessage, SnackTypes.error);
      setIsDirty(false);
      setIsSaving(false);
      ErrorLogger.log(error, 'NewSellerProfileEditPage.saveProfile');
    }
  };

  // Handle new seller registration submission
  const handleSubmitClick = () => {
    if (isNewSeller) {
      // Validate required fields for new seller
      const validationErrors: string[] = [];
      
      if (!fullName.trim()) validationErrors.push("Full Name");
      if (!dateOfBirth) validationErrors.push("Date of Birth");
      if (!gender) validationErrors.push("Gender");
      if (!contactNumber || !/^[6-9]\d{9}$/.test(contactNumber)) {
        validationErrors.push("Valid Mobile Number");
        if (contactNumber) setContactNumberError("Invalid mobile number");
      }
      if (!occupation) validationErrors.push("Occupation");
      if (!personalDescription.trim()) validationErrors.push("Personal Description");
      if (!tagline.trim()) validationErrors.push("Tagline");
      if (!sellerFormData.location.pincode) validationErrors.push("Pincode");
      if (!sellerFormData.location.region) validationErrors.push("Region");
      if (!sellerFormData.location.country) validationErrors.push("Country");
      if (!sellerFormData.location.state) validationErrors.push("State");
      if (sellerFormData.location.languages.length === 0) validationErrors.push("At least one Language");

      if (validationErrors.length > 0) {
        const errorMessage = `Please fill in the following required fields: ${validationErrors.join(", ")}`;
        showSnackbar(errorMessage, SnackTypes.error);
        return;
      }

      // Show user agreement modal
      setShowUserAgreementModal(true);
    } else {
      // Regular save changes flow
      handleSaveChanges();
    }
  };

  const handleRegisterNewSeller = async () => {
    try {
      setIsRegistering(true);
      setShowUserAgreementModal(false);

      // Prepare seller data
      const sellerData: any = {
        fullName: fullName.trim(),
        personalDescription: personalDescription.trim(),
        tagline: tagline.trim(),
        occupation,
        contactNumber,
        gender,
        dateOfBirth,
        userAgreement: true,
        location: {
          country: sellerFormData.location.country,
          state: sellerFormData.location.state,
          region: sellerFormData.location.region,
          pincode: parseInt(sellerFormData.location.pincode, 10),
          languages: sellerFormData.location.languages,
        },
      };

      // Handle profile image upload if exists
      if (uploadedProfileImage) {
        const compressedProfileImage = await compressImage(uploadedProfileImage, 0.05);
        const profileAsset = await new ResumableUploadTask(compressedProfileImage, { purpose: UploadPurpose.SELLER_PROFILE_PHOTO }).start();
        sellerData.profileImage = profileAsset.url;
        sellerData.profileImageAssetId = profileAsset.assetId;
      } else if (profileImage) {
        sellerData.profileImage = profileImage;
      }

      // Handle cover image upload if exists
      if (uploadedCoverImage) {
        const compressedCoverImage = await compressImage(uploadedCoverImage, 0.05);
        const coverAsset = await new ResumableUploadTask(compressedCoverImage, { purpose: UploadPurpose.SELLER_COVER_IMAGE }).start();
        sellerData.coverImage = coverAsset.url;
        sellerData.coverImageAssetId = coverAsset.assetId;
      } else if (coverImage) {
        sellerData.coverImage = coverImage;
      }

      // Add education if provided (optional)
      if (education.length > 0 && education.some(edu => edu.institute || edu.degree)) {
        sellerData.education = education
          .filter(edu => edu.institute && edu.degree && edu.passoutYear)
          .map((edu) => ({
            institute: edu.institute,
            degree: edu.degree,
            passoutYear: parseInt(edu.passoutYear, 10),
          }));
      } else {
        sellerData.education = [];
      }

      // Add work experience if provided (optional)
      if (workExperience.length > 0 && workExperience.some(exp => exp.company || exp.designation)) {
        sellerData.workExperience = workExperience
          .filter(exp => exp.company && exp.designation && exp.employmentType && exp.startDate)
          .map((exp) => ({
            company: exp.company,
            designation: exp.designation,
            employmentType: exp.employmentType,
            startDate: new Date(exp.startDate),
            endDate: exp.endDate ? new Date(exp.endDate) : undefined,
            currentlyWorking: exp.currentlyWorking || false,
          }));
      } else {
        sellerData.workExperience = [];
      }

      // Add skills if provided (optional)
      if (skills.length > 0 && skills.some(skill => skill.category && skill.subcategory)) {
        sellerData.skills = skills
          .filter(skill => skill.category && skill.subcategory)
          .map((skill) => ({
            category: skill.category,
            subcategory: skill.subcategory,
            tags: skill.tags,
            projects: skill.projects
              .filter(proj => proj.title && proj.description)
              .map((proj) => ({
                projectTitle: proj.title,
                projectDescription: proj.description,
                projectLink: proj.link,
                startDate: proj.startDate ? new Date(proj.startDate) : new Date(),
                endDate: proj.endDate ? new Date(proj.endDate) : new Date(),
              })),
            extraNotes: "",
          }));
      } else {
        sellerData.skills = [];
      }

      // Call create seller API
      await SellerAPIHandler.shared.createSeller(sellerData);

      setIsRegistering(false);
      setShowSuccessModal(true);

    } catch (error: any) {
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || error?.response?.data?.message || "Registration failed. Please try again.";
      
      showSnackbar(errorMessage, SnackTypes.error);
      setIsRegistering(false);
      ErrorLogger.log(error, 'NewSellerProfileEditPage.handleSubmitNewSeller');
    }
  };

  const handleSuccessModalButtonClick = () => {
    // Skip unsaved-changes beforeunload prompt for this intentional reload
    skipUnsavedChangesWarningRef.current = true;
    window.location.href = "/freelancer-dashboard";
  };

  const addRecord = (type: "education" | "workExperience") => {
    if (type === "education") {
      const newRecord: EducationProps = {
        id: uuidv4(),
        institute: "",
        degree: "",
        passoutYear: "",
      };
      setEducation([...education, newRecord]);
      setNewlyAddedEducationId(newRecord.id);
    } else {
      const newRecord: ExperienceProps = {
        id: uuidv4(),
        company: "",
        designation: "",
        employmentType: "",
        startDate: "",
        endDate: "",
        currentlyWorking: false,
      };
      setWorkExperience([...workExperience, newRecord]);
      setNewlyAddedExperienceId(newRecord.id);
    }
    setIsDirty(true);
  };

  const handleEducationChange = (id: string, field: string, value: string) => {
    setEducation((prev) =>
      prev.map((edu) => (edu.id === id ? { ...edu, [field]: value } : edu))
    );
    setIsDirty(true);
  };

  const handleExperienceChange = (
    index: number,
    field: string,
    value: string | boolean
  ) => {
    setWorkExperience((prev) =>
      prev.map((exp, i) => (i === index ? { ...exp, [field]: value } : exp))
    );
    setIsDirty(true);
  };

  const addNewSkill = () => {
    const newSkill = {
      id: uuidv4(),
      category: "",
      subcategory: "",
      tags: [],
      subcategories: [],
      projects: [
        {
          id: "1",
          title: "",
          description: "",
          startDate: "",
          endDate: "",
          link: "",
        },
      ],
    };
    setSkills([...skills, newSkill]);
    setNewlyAddedSkillId(newSkill.id);
    // Auto-expand the newly added skill
    setExpandedSkills((prev) => [...prev, newSkill.id]);
    setIsDirty(true);
  };

  const toggleSkillExpand = (skillId: string) => {
    setExpandedSkills((prev) =>
      prev.includes(skillId)
        ? prev.filter((id) => id !== skillId)
        : [...prev, skillId]
    );
  };

  const addNewProject = (skillId: string) => {
    setSkills((prev) =>
      prev.map((skill) =>
        skill.id === skillId
          ? {
              ...skill,
              projects: [
                ...skill.projects,
                {
                  id: uuidv4(),
                  title: "",
                  description: "",
                  startDate: "",
                  endDate: "",
                  link: "",
                },
              ],
            }
          : skill
      )
    );
    setActiveStep(
      skills.find((skill) => skill.id === skillId)?.projects.length || 0
    );
    setIsDirty(true);
  };

  const handleInputChange = (
    skillId: string,
    projectId: string | null,
    field: any,
    value: string
  ) => {
    setSkills((prev) =>
      prev.map((skill) =>
        skill.id === skillId
          ? {
              ...skill,
              ...(projectId
                ? {
                    projects: skill.projects.map((project) =>
                      project.id === projectId
                        ? { ...project, [field]: value }
                        : project
                    ),
                  }
                : { [field]: value }),
            }
          : skill
      )
    );
    if (field === "category" || field === "subcategory") setIsDirty(true);
    else setIsDirty(true);
  };

  const handleTagChange = (skillId: string, tags: string[]) => {
    setSkills((prev) =>
      prev.map((skill) => (skill.id === skillId ? { ...skill, tags } : skill))
    );
    setIsDirty(true);
  };

  const handleProfilePhotoUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setUploadedProfileImage(file);
      setProfileImage(objectUrl);
      setIsDirty(true);
    }
  };

  const handleCoverPhotoUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setUploadedCoverImage(file);
      setCoverImage(objectUrl);
      setIsDirty(true);
    }
  };

  const triggerProfileFileUpload = () => {
    profileFileInputRef.current?.click();
  };

  const triggerCoverFileUpload = () => {
    coverFileInputRef.current?.click();
  };

  const handleProfileImageChange = async () => {
    if (!sellerObj) return;

    const originalProfileImage = sellerObj.profileImage || "";
    const currentProfileImage = profileImage || "";

    if (originalProfileImage === currentProfileImage) return;

    if (originalProfileImage && originalProfileImage !== "") {
      await handleFileDeletion(originalProfileImage);
    }

    if (uploadedProfileImage && currentProfileImage) {
      const compressedImage = await compressImage(uploadedProfileImage, 0.05);
      await handleFileUpload(compressedImage, "profile");
    } else {
      sellerObj.profileImage = currentProfileImage;
    }
  };

  const handleCoverImageChange = async () => {
    if (!sellerObj) return;

    const originalCoverImage = sellerObj.coverImage || "";
    const currentCoverImage = coverImage || "";

    if (originalCoverImage === currentCoverImage) return;

    if (originalCoverImage && originalCoverImage !== "") {
      await handleFileDeletion(originalCoverImage);
    }

    if (uploadedCoverImage && currentCoverImage) {
      const compressedImage = await compressImage(uploadedCoverImage, 0.05);
      await handleFileUpload(compressedImage, "cover");
    } else {
      sellerObj.coverImage = currentCoverImage;
    }
  };

  const handleFileUpload = async (file: File, type: "profile" | "cover") => {
    if (!sellerObj) return;

    const asset = await new ResumableUploadTask(file, {
      purpose: type === "profile"
        ? UploadPurpose.SELLER_PROFILE_PHOTO
        : UploadPurpose.SELLER_COVER_IMAGE,
    }).start();

    if (type === "profile") {
      setProfileImage(asset.url || "");
      sellerObj.profileImage = asset.url || "";
      sellerObj.profileImageAssetId = asset.assetId;
    } else {
      setCoverImage(asset.url || "");
      sellerObj.coverImage = asset.url || "";
      sellerObj.coverImageAssetId = asset.assetId;
    }
  };

  const handleFileDeletion = async (_fileUrl: string) => {};

  const handleRemoveProfileImage = () => {
    setUploadedProfileImage(undefined);
    setProfileImage("");
    setIsDirty(true);
  };

  const handleRemoveCoverImage = () => {
    setUploadedCoverImage(undefined);
    setCoverImage("");
    setIsDirty(true);
  };

  const fetchCategoriesAndSubcategories = async () => {
    CategoryAPIHandler.shared
      .getAllCategories()
      .then((categoryResp) => {
        setCategoriesList(categoryResp.categories);

        const fetchAllSubcategories = async (categories: Category[]) => {
          const newCategoriesMap = new Map<string, Subcategory[]>();

          for (const category of categories) {
            try {
              const response =
                await SubcategoryAPIHandler.shared.getSubcategoriesForCategory(
                  category.id,
                  1,
                  Number.MAX_SAFE_INTEGER
                );
              newCategoriesMap.set(
                category.title,
                response.subcategories.map((subcat: any) => subcat)
              );
            } catch (error) {
              ErrorLogger.log(error, `NewSellerProfileEditPage.fetchAllSubcategories[${category.title}]`);
            }
          }

          setCategoriesMap(newCategoriesMap);
        };

        fetchAllSubcategories(categoryResp.categories);
      })
      .catch((err) => {
        showSnackbar(err, SnackTypes.error);
      });
  };

  const [sellerObj, setSellerObj] = useState<Seller>();

  useEffect(() => {
    // Skip fetching seller data if registering new seller
    if (isNewSeller) {
      // Initialize with default values for new seller
      setInstitutes(indianInstitutes);
      setDegrees(customDegrees);
      fetchCategoriesAndSubcategories();
      
      // Mark initial load as complete after a delay
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
        setIsDirty(true); // Enable the submit button for new seller
      }, 500);
      
      return () => clearTimeout(timer);
    }

    const sellerId = SessionManager?.shared?.buyer?.seller;
    if (sellerId) {
      SellerAPIHandler.shared.getSellerById(sellerId).then((response) => {
        setSellerObj(response);
        setProfileImage(response.profileImage || "");
        setCoverImage(response.coverImage || "");
        setGender(response.gender || "");
        setOccupation(response.occupation);

        setFullName(response.fullName || "");
        setPersonalDescription(response.personalDescription || "");
        setTagline(response.tagline || "");
        setContactNumber(response.contactNumber || "");
        setDateOfBirth(
          new Date(response.dateOfBirth).toISOString().split("T")[0]
        );

        const locationData = {
          country: response.location?.country || "",
          state: response.location?.state || "",
          region: response.location?.region || "",
          pincode: String(response.location?.pincode || ""),
          languages: response.location?.languages || [],
        };
        setSellerFormData((prevData) => ({
          ...prevData,
          location: locationData,
        }));

        const apiInstitutes: string[] = Array.from(
          new Set(response.education?.map((edu) => edu.institute || ""))
        );

        const apiDegrees = Array.from(
          new Set(response.education?.map((edu) => edu.degree || "")) ?? []
        );

        const mergedInstitutes = Array.from(
          new Set([...apiInstitutes, ...indianInstitutes])
        );

        const mergedDegrees = Array.from(
          new Set([...apiDegrees, ...customDegrees])
        );

        setInstitutes(mergedInstitutes);
        setDegrees(mergedDegrees);

        const educationData =
          response.education?.map((edu) => ({
            id: uuidv4(),
            institute: edu.institute || "",
            degree: edu.degree || "",
            passoutYear: String(edu.passoutYear || ""),
          })) ?? [];
        setEducation(educationData);

        const workExperienceData =
          response.workExperience?.map((exp) => ({
            id: uuidv4(),
            company: exp.company || "",
            designation: exp.designation || "",
            employmentType: exp.employmentType || "",
            startDate: new Date(exp.startDate).toISOString().split("T")[0],
            endDate: exp.endDate ? new Date(exp.endDate).toISOString().split("T")[0] : undefined,
            currentlyWorking: exp.currentlyWorking || false,
          })) ?? [];
        setWorkExperience(workExperienceData);

        fetchCategoriesAndSubcategories()
          .then(() => {
            const skillsData =
              response.skills?.map((skill) => ({
                id: uuidv4(),
                category: skill.category || "",
                subcategory: skill.subcategory || "",
                tags: [],
                subcategories: [],
                projects: skill.projects.map((project) => ({
                  id: uuidv4(),
                  title: project.projectTitle || "",
                  description: project.projectDescription || "",
                  startDate: new Date(project.startDate)
                    .toISOString()
                    .split("T")[0],
                  endDate: new Date(project.endDate)
                    .toISOString()
                    .split("T")[0],
                  link: project.projectLink || "",
                })),
              })) ?? [];
            setSkills(skillsData);
          })
          .catch((error) => {
            ErrorLogger.log(error, 'NewSellerProfileEditPage.fetchCategoriesAndSubcategories');
            showSnackbar(
              "Failed to load categories and subcategories: " + error,
              SnackTypes.error
            );
          });
      });
    }

    // Mark initial load as complete after a delay to ensure all components (like LocationForm) have finished initializing
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  // Handle page refresh/navigation warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Skip native prompt for intentional programmatic reloads
      if (skipUnsavedChangesWarningRef.current) {
        return;
      }

      if (isDirty) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // For older browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  // Handle refresh button click (Ctrl+R, Cmd+R, F5)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Detect Ctrl+R, Cmd+R, or F5
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        if (isDirty) {
          e.preventDefault();
          setShowRefreshDialog(true);
        }
      } else if (e.key === 'F5') {
        if (isDirty) {
          e.preventDefault();
          setShowRefreshDialog(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDirty]);

  // useEffect(() => {
  //   setIsLoaded(true);
  // }, []); // Not used in new design

  useEffect(() => {
    if (categoriesMap.size > 0 && skills.length > 0) {
      setSkills((prevSkills) =>
        prevSkills.map((skill) => {
          if (skill.category && skill.subcategories.length === 0) {
            const catObj = categoriesList.find(
              (cat) => cat.id === skill.category
            );
            if (catObj) {
              const subCats = categoriesMap.get(catObj.title);
              return { ...skill, subcategories: subCats ?? [] };
            }
          }
          return skill;
        })
      );
    }
  }, [categoriesMap, skills.length, categoriesList]);

  // Scroll to newly added education
  useEffect(() => {
    if (newlyAddedEducationId && educationRefs.current[newlyAddedEducationId]) {
      const element = educationRefs.current[newlyAddedEducationId];
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setNewlyAddedEducationId(null);
        }, 100);
      }
    }
  }, [newlyAddedEducationId, education]);

  // Scroll to newly added experience
  useEffect(() => {
    if (newlyAddedExperienceId && experienceRefs.current[newlyAddedExperienceId]) {
      const element = experienceRefs.current[newlyAddedExperienceId];
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setNewlyAddedExperienceId(null);
        }, 100);
      }
    }
  }, [newlyAddedExperienceId, workExperience]);

  // Scroll to newly added skill
  useEffect(() => {
    if (newlyAddedSkillId && skillRefs.current[newlyAddedSkillId]) {
      const element = skillRefs.current[newlyAddedSkillId];
      if (element) {
        // Wait for the expand animation to complete, then scroll with calculated offset
        setTimeout(() => {
          const elementRect = element.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          // Account for sticky navbar (~60px) and save button bar (~76px) = ~136px total
          const offset = 150;
          const scrollPosition = absoluteElementTop - offset;
          
          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
          setNewlyAddedSkillId(null);
        }, 400); // Wait for expand animation to complete
      }
    }
  }, [newlyAddedSkillId, skills]);

  const handleDeleteEducation = (id: string) => {
    setEducation((prev) => {
      return prev.filter((edu) => edu.id !== id);
    });
    setIsDirty(true);
  };

  const handleDeleteExperience = (id: string) => {
    setWorkExperience((prev) => prev.filter((exp) => exp.id !== id));
    setIsDirty(true);
  };

  const handleDeleteSkill = (id: string) => {
    setSkills((prevSkills) => {
      const updatedSkills = prevSkills.filter((skill) => skill.id !== id);
      if (activeStep >= updatedSkills.length) {
        setActiveStep(updatedSkills.length - 1);
      }
      return updatedSkills;
    });
    setIsDirty(true);
  };

  const handleDeleteProject = (skillId: string, projectId: string) => {
    setSkills((prev) =>
      prev.map((skill) =>
        skill.id === skillId
          ? {
              ...skill,
              projects: skill.projects.filter(
                (project) => project.id !== projectId
              ),
            }
          : skill
      )
    );
    setIsDirty(true);
  };

  const isProjectComplete = (project: any) => {
    return (
      project.title &&
      project.description &&
      project.startDate &&
      project.endDate &&
      project.link
    );
  };

  return (
    <>
      <Navbar useScrollVersion={true} />
      <div className="new-profile-edit-page">
        {/* Sticky Save Button */}
        <Box className="save-button-container">
          {isDirty && (
            <Typography variant="body2" className="unsaved-changes-text">
              * You have unsaved changes
            </Typography>
          )}
          <GGLoaderButton
            colorHex="#2e7d32"
            variant="contained"
            className="save-changes-btn"
            disabled={!isDirty || isSaving || isRegistering}
            onClick={handleSubmitClick}
            isLoading={isSaving || isRegistering}
          >
            {isNewSeller ? "Register as Freelancer" : "Save Changes"}
          </GGLoaderButton>
        </Box>

        <div className="new-profile-edit-container">
          {/* Profile Header Section */}
          <Box className="profile-header-section">
            {/* Cover Image */}
            <Box className="cover-image-section">
              <GGPlaceholderImage
                src={coverImage}
                alt="cover"
                className="cover-image-preview"
                placeholder={
                  <Box
                    className="cover-image-placeholder"
                    sx={{
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
                    }}
                  />
                }
              />
              <Box className="cover-image-overlay">
                <IconButton
                  className="cover-edit-button"
                  onClick={triggerCoverFileUpload}
                >
                  <PhotoCamera />
                </IconButton>
                {coverImage && (
                  <IconButton
                    className="cover-delete-button"
                    onClick={handleRemoveCoverImage}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>

            {/* Profile Avatar */}
            <Box className="profile-avatar-section">
              <Avatar
                alt="Profile Picture"
                src={profileImage || undefined}
                className="profile-avatar"
                sx={{
                  width: 152,
                  height: 152,
                  bgcolor: "#002f6c",
                  fontSize: "64px",
                  fontWeight: 600,
                }}
              >
                {fullName?.[0]?.toUpperCase() || "U"}
              </Avatar>
              <IconButton
                className="avatar-edit-button"
                onClick={triggerProfileFileUpload}
              >
                <PhotoCamera />
              </IconButton>
              {profileImage && (
                <IconButton
                  className="avatar-delete-button"
                  onClick={handleRemoveProfileImage}
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
            {/* <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center', 
                color: '#666', 
                paddingBottom: '16px',
                fontStyle: 'italic'
              }}
            >
              Profile photo and cover image are optional
            </Typography> */}
          </Box>

          {/* Form Sections */}
          <Grid container spacing={3} className="form-sections">
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              {/* Personal Details */}
              <Box className="section-card">
                <Box className="section-header">
                  <Typography variant="h6" fontWeight={600}>
                    Personal Details
                  </Typography>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        variant="outlined"
                        value={fullName}
                        onChange={(e) => {
                          setFullName(e.target.value);
                          setIsDirty(true);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={dateOfBirth}
                        onChange={(e) => {
                          setDateOfBirth(e.target.value);
                          setIsDirty(true);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>Gender</InputLabel>
                        <Select
                          value={gender}
                          label="Gender"
                          onChange={(e) => {
                            setGender(e.target.value);
                            setIsDirty(true);
                          }}
                        >
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Mobile Number"
                        type="tel"
                        value={contactNumber}
                        error={contactNumberError !== ""}
                        helperText={contactNumberError}
                        inputProps={{ maxLength: 10 }}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 10) {
                            setContactNumber(value);
                            if (contactNumberError) setContactNumberError("");
                            setIsDirty(true);
                          }
                        }}
                        onBlur={() => {
                          if (!/^[6-9]\d{9}$/.test(contactNumber)) {
                            setContactNumberError("Invalid mobile number");
                          } else {
                            setContactNumberError("");
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Occupation</InputLabel>
                        <Select
                          value={occupation}
                          label="Occupation"
                          onChange={(e) => {
                            setOccupation(e.target.value);
                            setIsDirty(true);
                          }}
                        >
                          <MenuItem value="Student">Student</MenuItem>
                          <MenuItem value="Working Professional">Working Professional</MenuItem>
                          <MenuItem value="Home-maker">Home-maker</MenuItem>
                          <MenuItem value="Others">Others</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Tagline"
                        multiline
                        rows={2}
                        value={tagline}
                        inputProps={{ maxLength: 200 }}
                        helperText={`${tagline.length}/200 characters`}
                        onChange={(e) => {
                          setTagline(e.target.value);
                          setIsDirty(true);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Personal Description"
                        multiline
                        rows={4}
                        value={personalDescription}
                        inputProps={{ maxLength: 4000 }}
                        helperText={`${personalDescription.length}/4000 characters`}
                        onChange={(e) => {
                          setPersonalDescription(e.target.value);
                          setIsDirty(true);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              {/* Education */}
              <Box className="section-card">
                <Box className="section-header">
                  <Typography variant="h6" fontWeight={600}>
                    Education <span style={{ fontWeight: 400, fontSize: '0.875rem', color: '#666' }}>(Optional)</span>
                  </Typography>
                  <GGButton
                    btnColor="#002f6c"
                    variant="outlined"
                    onClick={() => addRecord("education")}
                    startIcon={<Add />}
                  >
                    Add Education
                  </GGButton>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                  {education.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No education added. Click "Add Education" to add one.
                    </Typography>
                  ) : (
                    education.map((edu, index) => (
                      <Box 
                        key={edu.id} 
                        ref={(el: HTMLDivElement | null) => { educationRefs.current[edu.id] = el; }}
                        className="education-item" 
                        mb={index < education.length - 1 ? 3 : 0}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Education {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteEducation(edu.id)}
                            color="error"
                          >
                            <DeleteOutlinedIcon />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Autocomplete
                              fullWidth
                              options={institutes}
                              value={edu.institute}
                              onChange={(_, newValue) =>
                                handleEducationChange(
                                  edu.id,
                                  "institute",
                                  newValue || ""
                                )
                              }
                              ListboxComponent={
                                ListboxComponent as React.ComponentType<
                                  React.HTMLAttributes<HTMLElement>
                                >
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Institute"
                                  variant="outlined"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Autocomplete
                              fullWidth
                              options={degrees}
                              value={edu.degree}
                              onChange={(_, newValue) =>
                                handleEducationChange(
                                  edu.id,
                                  "degree",
                                  newValue || ""
                                )
                              }
                              ListboxComponent={
                                ListboxComponent as React.ComponentType<
                                  React.HTMLAttributes<HTMLElement>
                                >
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Degree"
                                  variant="outlined"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Passing Year"
                              variant="outlined"
                              value={edu.passoutYear}
                              onChange={(e) =>
                                handleEducationChange(
                                  edu.id,
                                  "passoutYear",
                                  e.target.value
                                )
                              }
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>

              {/* Languages & Location */}
              <Box className="section-card">
                <Box className="section-header">
                  <Typography variant="h6" fontWeight={600}>
                    Location & Languages
                  </Typography>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                  <LocationForm
                    sellerFormData={sellerFormData}
                    setSellerFormData={(data: SellerFormData) => {
                      setSellerFormData(data);
                      // Only set dirty if not initial load
                      if (!isInitialLoad) {
                        setIsDirty(true);
                      }
                    }}
                    gridItemSx={{ width: "100%" }}
                    languageStyles={{
                      textAlign: "left",
                      display: "flex",
                      flexDirection: "row",
                      fontWeight: "bold",
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              {/* Work Experience */}
              <Box className="section-card">
                <Box className="section-header">
                  <Typography variant="h6" fontWeight={600}>
                    Work Experience <span style={{ fontWeight: 400, fontSize: '0.875rem', color: '#666' }}>(Optional)</span>
                  </Typography>
                  <GGButton
                    btnColor="#002f6c"
                    variant="outlined"
                    onClick={() => addRecord("workExperience")}
                    startIcon={<Add />}
                  >
                    Add Experience
                  </GGButton>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                  {workExperience.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No work experience added. Click "Add Experience" to add one.
                    </Typography>
                  ) : (
                    workExperience.map((experience, index) => (
                      <Box 
                        key={experience.id} 
                        ref={(el: HTMLDivElement | null) => { experienceRefs.current[experience.id] = el; }}
                        className="experience-item" 
                        mb={index < workExperience.length - 1 ? 3 : 0}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Experience {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteExperience(experience.id)}
                            color="error"
                          >
                            <DeleteOutlinedIcon />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Company"
                              variant="outlined"
                              value={experience.company}
                              onChange={(e) =>
                                handleExperienceChange(
                                  index,
                                  "company",
                                  e.target.value
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Designation"
                              variant="outlined"
                              value={experience.designation}
                              onChange={(e) =>
                                handleExperienceChange(
                                  index,
                                  "designation",
                                  e.target.value
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Employment Type</InputLabel>
                              <Select
                                value={experience.employmentType}
                                label="Employment Type"
                                onChange={(e) =>
                                  handleExperienceChange(
                                    index,
                                    "employmentType",
                                    e.target.value
                                  )
                                }
                              >
                                <MenuItem value="Full-time">Full-time</MenuItem>
                                <MenuItem value="Part-time">Part-time</MenuItem>
                                <MenuItem value="Contract">Contract</MenuItem>
                                <MenuItem value="Internship">Internship</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Start Date"
                              type="date"
                              InputLabelProps={{ shrink: true }}
                              value={experience.startDate}
                              onChange={(e) =>
                                handleExperienceChange(
                                  index,
                                  "startDate",
                                  e.target.value
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="End Date"
                              type="date"
                              InputLabelProps={{ shrink: true }}
                              value={experience.endDate || ""}
                              disabled={experience.currentlyWorking || false}
                              onChange={(e) =>
                                handleExperienceChange(
                                  index,
                                  "endDate",
                                  e.target.value
                                )
                              }
                            />
                          </Grid>
                        </Grid>
                        <Box sx={{ mt: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={experience.currentlyWorking || false}
                                onChange={(e) => {
                                  const isCurrentlyWorking = e.target.checked;
                                  handleExperienceChange(
                                    index,
                                    "currentlyWorking",
                                    isCurrentlyWorking
                                  );
                                  // Clear end date when currently working is checked
                                  if (isCurrentlyWorking) {
                                    handleExperienceChange(
                                      index,
                                      "endDate",
                                      ""
                                    );
                                  }
                                }}
                                inputProps={{ 'aria-label': 'Currently Working' }}
                              />
                            }
                            label="Currently Working Here"
                          />
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>

              {/* Skills */}
              <Box className="section-card">
                <Box className="section-header">
                  <Typography variant="h6" fontWeight={600}>
                    Skills <span style={{ fontWeight: 400, fontSize: '0.875rem', color: '#666' }}>(Optional)</span>
                  </Typography>
                  <GGButton
                    btnColor="#002f6c"
                    variant="outlined"
                    onClick={addNewSkill}
                    startIcon={<Add />}
                  >
                    Add Skill
                  </GGButton>
                </Box>
                <Divider className="section-divider" />
                <Box className="section-content" pt={2}>
                  {skills.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No skills added. Click "Add Skill" to add one.
                    </Typography>
                  ) : (
                    skills.map((skill, skillIndex) => (
                      <Box 
                        key={skill.id} 
                        ref={(el: HTMLDivElement | null) => { skillRefs.current[skill.id] = el; }}
                        className="skill-item" 
                        mb={skillIndex < skills.length - 1 ? 3 : 0}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Skill {skillIndex + 1}
                          </Typography>
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              onClick={() => toggleSkillExpand(skill.id)}
                            >
                              {expandedSkills.includes(skill.id) ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSkill(skill.id)}
                              color="error"
                            >
                              <DeleteOutlinedIcon />
                            </IconButton>
                          </Box>
                        </Box>
                        <Collapse
                          in={expandedSkills.includes(skill.id)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                  value={skill.category}
                                  label="Category"
                                  onChange={async (event) => {
                                    const selectedCategoryId = event.target.value;
                                    handleInputChange(
                                      skill.id,
                                      null,
                                      "category",
                                      selectedCategoryId
                                    );
                                    handleInputChange(
                                      skill.id,
                                      null,
                                      "subcategory",
                                      ""
                                    );
                                    setIsDirty(true);
                                    const selectedCategory = categoriesList.find(
                                      (cat) => cat.id === selectedCategoryId
                                    );
                                    if (selectedCategory) {
                                      const subCats = categoriesMap.get(
                                        selectedCategory.title
                                      );
                                      setSkills((prevSkills) =>
                                        prevSkills.map((s) =>
                                          s.id === skill.id
                                            ? {
                                                ...s,
                                                subcategories: subCats ?? [],
                                              }
                                            : s
                                        )
                                      );
                                    }
                                  }}
                                >
                                  <MenuItem value="">Select Category</MenuItem>
                                  {categoriesList.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                      {cat.title}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <InputLabel>Subcategory</InputLabel>
                                <Select
                                  value={skill.subcategory}
                                  label="Subcategory"
                                  onChange={(event) => {
                                    handleInputChange(
                                      skill.id,
                                      null,
                                      "subcategory",
                                      event.target.value
                                    );
                                    setIsDirty(true);
                                  }}
                                >
                                  <MenuItem value="">Select Subcategory</MenuItem>
                                  {skill.subcategories.map((subCat) => (
                                    <MenuItem key={subCat.id} value={subCat.id}>
                                      {subCat.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                              <Autocomplete
                                multiple
                                freeSolo
                                options={[]}
                                value={skill.tags}
                                onChange={(_, newValue) =>
                                  handleTagChange(skill.id, newValue)
                                }
                                renderTags={(value: readonly string[], getTagProps) =>
                                  value.map((option: string, index: number) => (
                                    <Chip
                                      variant="outlined"
                                      label={option}
                                      {...getTagProps({ index })}
                                      key={index}
                                    />
                                  ))
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Tags"
                                    placeholder="Add tag"
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>

                          {/* Projects */}
                          <Box className="projects-section">
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                Projects
                              </Typography>
                              <GGButton
                                btnColor="#002f6c"
                                variant="outlined"
                                size="small"
                                onClick={() => addNewProject(skill.id)}
                                startIcon={<Add />}
                              >
                                Add Project
                              </GGButton>
                            </Box>
                            <Stepper activeStep={activeStep} orientation="vertical">
                              {skill.projects.map((project, projectIndex) => (
                                <Step key={project.id}>
                                  <StepLabel
                                    StepIconComponent={() => (
                                      <Box
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: "50%",
                                          bgcolor: isProjectComplete(project)
                                            ? "#4caf50"
                                            : "#e0e0e0",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "white",
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {projectIndex + 1}
                                      </Box>
                                    )}
                                  >
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Typography variant="subtitle2" fontWeight={600}>
                                        Project {projectIndex + 1}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDeleteProject(skill.id, project.id)
                                        }
                                        color="error"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </StepLabel>
                                  <StepContent>
                                    <Box className="project-form">
                                      <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                          <TextField
                                            fullWidth
                                            label="Project Title"
                                            value={project.title}
                                            onChange={(e) =>
                                              handleInputChange(
                                                skill.id,
                                                project.id,
                                                "title",
                                                e.target.value
                                              )
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <TextField
                                            fullWidth
                                            label="Project Description"
                                            value={project.description}
                                            onChange={(e) =>
                                              handleInputChange(
                                                skill.id,
                                                project.id,
                                                "description",
                                                e.target.value
                                              )
                                            }
                                            multiline
                                            rows={3}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <TextField
                                            fullWidth
                                            label="Start Date"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={project.startDate}
                                            onChange={(e) =>
                                              handleInputChange(
                                                skill.id,
                                                project.id,
                                                "startDate",
                                                e.target.value
                                              )
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <TextField
                                            fullWidth
                                            label="End Date"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={project.endDate}
                                            onChange={(e) =>
                                              handleInputChange(
                                                skill.id,
                                                project.id,
                                                "endDate",
                                                e.target.value
                                              )
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <TextField
                                            fullWidth
                                            label="Project Link"
                                            type="url"
                                            value={project.link}
                                            onChange={(e) =>
                                              handleInputChange(
                                                skill.id,
                                                project.id,
                                                "link",
                                                e.target.value
                                              )
                                            }
                                          />
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  </StepContent>
                                </Step>
                              ))}
                            </Stepper>
                          </Box>
                        </Collapse>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* Hidden File Inputs */}
      <input
        type="file"
        ref={profileFileInputRef}
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleProfilePhotoUpload}
      />
      <input
        type="file"
        ref={coverFileInputRef}
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleCoverPhotoUpload}
      />

      <Footer />
      <SnackBar
        ref={snackBarRef}
        severity={snackBarParams.state}
        alertDuration={5000}
        message={snackBarParams.message}
      />
      <GGDialogueBox
        type={dialogueType.Alert}
        isOpen={showRefreshDialog}
        message="You have unsaved changes. Are you sure you want to refresh the page? All unsaved changes will be lost."
        successHandler={() => {
          setShowRefreshDialog(false);
          // Skip native unsaved-changes prompt for this intentional reload
          skipUnsavedChangesWarningRef.current = true;
          window.location.reload();
        }}
        closeHandler={() => {
          setShowRefreshDialog(false);
        }}
      />

      {/* User Agreement Modal for New Seller Registration */}
      <UserAgreementModal
        isOpen={showUserAgreementModal}
        onClose={() => setShowUserAgreementModal(false)}
        onAgree={handleRegisterNewSeller}
        isLoading={isRegistering}
      />

      {/* Success Modal for New Seller Registration */}
      <Modal
        open={showSuccessModal}
        onClose={() => {}}
        disableEscapeKeyDown
        className="success-modal-overlay"
      >
        <Box className="success-modal-container">
          <CongratulationsPageForGigReg
            title="Congratulations!"
            description="You have successfully registered as a Freelancer on GenieGig."
            buttonText="Explore Dashboard!"
            buttonIcon={<HomeIcon />}
            onClickHandler={handleSuccessModalButtonClick}
          />
        </Box>
      </Modal>
    </>
  );
};

export default SellerProfileEditPage;

